(function () {
    const TRIGGER_SELECTOR = '.tooltip-trigger';
    const TOOLTIP_SELECTOR = '.tooltip-text';
    const CLOSE_SELECTOR = '.closeTooltip';
    const VEIL_SELECTOR = '.veil';
    const OPEN_CLASS = 'is-open';

    // Focusable elements selector (common controls + explicit tabindex)
    const FOCUSABLE_SELECTOR = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'summary',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const veil = document.querySelector(VEIL_SELECTOR);
    let currentOpenTrigger = null;
    let previouslyFocused = null;

    // Store handlers so we can cleanly remove them
    let trapKeydownHandler = null;

    // Polyfills
    function matches(el, sel) {
        if (!el) return false;
        const fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
        return fn ? fn.call(el, sel) : false;
    }

    function closest(el, sel) {
        if (!el) return null;
        if (el.closest) return el.closest(sel);
        // manual fallback
        while (el && el.nodeType === 1) {
            if (matches(el, sel)) return el;
            el = el.parentElement;
        }
        return null;
    }

    function getTooltipForTrigger(trigger) {
        if (!trigger) return null;
        const sib = trigger.nextElementSibling;
        return (sib && matches(sib, TOOLTIP_SELECTOR)) ? sib : null;
    }

    function ensureId(el) {
        if (!el.id) {
            el.id = 'tooltip-' + Math.random().toString(36).slice(2, 9);
        }
        return el.id;
    }

    // You’re using jQuery for veil; keep it
    function showVeil() { $('.veil').show().attr('aria-hidden', 'false').removeAttr('hidden'); }
    function hideVeil() { $('.veil').hide().attr('aria-hidden', 'true').attr('hidden', ''); }

    function syncAria(trigger, tooltip, isOpen) {
        if (!trigger || !tooltip) return;
        const id = ensureId(tooltip);
        trigger.setAttribute('aria-controls', id);
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        tooltip.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

        if (!tooltip.hasAttribute('role')) {
            tooltip.setAttribute('role', 'tooltip');
        }

        if (isOpen) {
            trigger.setAttribute('aria-describedby', id);
        } else {
            if (trigger.getAttribute('aria-describedby') === id) {
                trigger.removeAttribute('aria-describedby');
            }
        }
    }

    // ---- Focus management helpers ----

    // Temporarily remove focus from all elements except the given allowed nodes
    function lockBackgroundFocus(allowedNodes) {
        const allFocusables = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR));
        const allowedSet = new Set();

        allowedNodes.forEach(node => {
            if (!node) return;
            allowedSet.add(node);
            // Include descendants of allowed nodes
            Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).forEach(n => allowedSet.add(n));
        });

        allFocusables.forEach(el => {
            if (allowedSet.has(el)) return; // keep
            // Preserve existing tabindex to restore later
            if (!el.hasAttribute('data-prev-tabindex')) {
                const prev = el.getAttribute('tabindex');
                if (prev !== null) el.setAttribute('data-prev-tabindex', prev);
                else el.setAttribute('data-prev-tabindex', ''); // marker for "no prior tabindex"
            }
            el.setAttribute('tabindex', '-1');
            el.setAttribute('data-focus-locked', 'true');
        });
    }

    // Restore tabindex where we changed it
    function unlockBackgroundFocus() {
        const locked = Array.from(document.querySelectorAll('[data-focus-locked="true"]'));
        locked.forEach(el => {
            const prev = el.getAttribute('data-prev-tabindex');
            if (prev === '') {
                el.removeAttribute('tabindex');
            } else if (prev !== null) {
                el.setAttribute('tabindex', prev);
            }
            el.removeAttribute('data-prev-tabindex');
            el.removeAttribute('data-focus-locked');
        });
    }

    // Trap Tab/Shift+Tab within the tooltip (and optionally allow trigger)
    function enableFocusTrap(tooltip, trigger) {

        disableFocusTrap();

        if (!hasInteractiveControls(tooltip)) {
            return;
        }

        trapKeydownHandler = function (e) {

            if (e.key !== 'Tab') {
                return;
            }

            const focusables =
                getFocusableInScope(tooltip);

            if (!focusables.length) {
                return;
            }

            const active =
                document.activeElement;

            const first =
                focusables[0];

            const last =
                focusables[focusables.length - 1];

            const tabbingForward =
                !e.shiftKey &&
                active === last;

            const tabbingBackward =
                e.shiftKey &&
                active === first;

            if (
                tabbingForward ||
                tabbingBackward
            ) {

                disableFocusTrap();

                setTimeout(function () {

                    closeTooltip(
                        trigger,
                        tooltip,
                        false
                    );

                }, 0);
            }
        };

        document.addEventListener(
            'keydown',
            trapKeydownHandler
        );
    }

    function disableFocusTrap() {
        if (trapKeydownHandler) {
            document.removeEventListener('keydown', trapKeydownHandler, true);
            trapKeydownHandler = null;
        }
    }

    function getFocusableInScope(tooltip) {

        return Array.from(
            tooltip.querySelectorAll(
                FOCUSABLE_SELECTOR
            )
        ).filter(uniqueVisible);
    }
    function hasInteractiveControls(tooltip) {
        if (!tooltip) return false;

        return tooltip.querySelector(
            'a[href], ' +
            'button:not([disabled]), ' +
            'input:not([disabled]):not([type="hidden"]), ' +
            'select:not([disabled]), ' +
            'textarea:not([disabled])'
        ) !== null;
    }

    function isElementFocusable(el) {
        if (!el) return false;
        const tabindex = el.getAttribute('tabindex');
        const disabled = el.getAttribute('disabled') !== null;
        if (disabled) return false;
        // If it has a valid tabindex or is a naturally focusable element
        return (tabindex !== null && tabindex !== '-1') || el.matches(FOCUSABLE_SELECTOR);
    }

    function uniqueVisible(el, idx, arr) {
        // dedupe
        if (arr.indexOf(el) !== idx) return false;
        // ensure visible (basic check)
        const cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') return false;
        return true;
    }

    // ---- Open/close ----

    function openTooltip(trigger, tooltip) {
        if (!tooltip) return;

        previouslyFocused = document.activeElement;

        tooltip.classList.add(OPEN_CLASS);
        // Defensive: ensure visible in case CSS is missing/overridden
        tooltip.style.display = 'block';
        tooltip.style.visibility = 'visible';
        tooltip.style.opacity = '1';

        syncAria(trigger, tooltip, true);
        showVeil();
        currentOpenTrigger = trigger;

        // Only apply trap logic when interactive controls exist
        if (hasInteractiveControls(tooltip)) {

            // REMOVE THIS
            // lockBackgroundFocus([tooltip, trigger]);

            enableFocusTrap(tooltip, trigger);
        }

        // 3) Move focus into tooltip (prefer close button, else first focusable, else tooltip itself)
        const closeBtn = tooltip.querySelector(CLOSE_SELECTOR);
        const focusTargets = getFocusableInScope(tooltip);
        if (closeBtn) {
            closeBtn.focus();
        } else if (focusTargets.length > 0) {
            focusTargets[0].focus();
        } else {
            tooltip.setAttribute('tabindex', '-1'); // make it focusable temporarily
            tooltip.focus();
        }
    }

    function closeTooltip(trigger, tooltip, restoreFocus = true) {
        if (!tooltip) return;
        tooltip.classList.remove(OPEN_CLASS);
        // Defensive: hide explicitly
        tooltip.style.display = 'none';
        tooltip.style.visibility = 'hidden';
        tooltip.style.opacity = '0';

        syncAria(trigger, tooltip, false);
        hideVeil();

        // Restore focus behaviors
        disableFocusTrap();
        //unlockBackgroundFocus();

        // Restore prior focus
        if (restoreFocus) {

            if (
                previouslyFocused &&
                typeof previouslyFocused.focus === 'function'
            ) {
                previouslyFocused.focus();
            } else if (
                trigger &&
                typeof trigger.focus === 'function'
            ) {
                trigger.focus();
            }
        }

        if (currentOpenTrigger === trigger) currentOpenTrigger = null;
        previouslyFocused = null;
    }

    function toggleTooltip(trigger) {
        const tooltip = getTooltipForTrigger(trigger);
        if (!tooltip) return;
        const isOpen = tooltip.classList.contains(OPEN_CLASS);
        if (isOpen) {
            closeTooltip(trigger, tooltip);
        } else {
            // Close any other open tooltip first
            if (currentOpenTrigger && currentOpenTrigger !== trigger) {
                const openTooltipEl = getTooltipForTrigger(currentOpenTrigger);
                closeTooltip(currentOpenTrigger, openTooltipEl);
            }
            openTooltip(trigger, tooltip);
        }
    }

    // CLICK: open/close via trigger; close via close button; click-outside closes
    document.addEventListener('click', function (e) {
        const trigger = closest(e.target, TRIGGER_SELECTOR);
        if (trigger) {
            toggleTooltip(trigger);
            return;
        }

        const closeBtn = closest(e.target, CLOSE_SELECTOR);
        if (closeBtn) {
            const tooltipEl = closest(closeBtn, TOOLTIP_SELECTOR);
            const relTrigger = tooltipEl && tooltipEl.previousElementSibling &&
                matches(tooltipEl.previousElementSibling, TRIGGER_SELECTOR)
                ? tooltipEl.previousElementSibling
                : null;
            closeTooltip(relTrigger, tooltipEl);
            return;
        }

        // Click outside: if clicking anywhere not inside the open tooltip/trigger, close
        if (currentOpenTrigger) {
            const openTooltipEl = getTooltipForTrigger(currentOpenTrigger);
            const clickedInsideOpen =
                closest(e.target, TOOLTIP_SELECTOR) === openTooltipEl ||
                closest(e.target, TRIGGER_SELECTOR) === currentOpenTrigger;

            if (!clickedInsideOpen) {
                closeTooltip(currentOpenTrigger, openTooltipEl);
            }
        }
    });
    document.addEventListener(
        'focusin',
        function (e) {

            if (!currentOpenTrigger) {
                return;
            }

            const tooltip =
                getTooltipForTrigger(
                    currentOpenTrigger
                );

            if (!tooltip) {
                return;
            }

            const insideTooltip =
                tooltip.contains(e.target);

            const insideTrigger =
                currentOpenTrigger.contains(e.target);

            if (
                !insideTooltip &&
                !insideTrigger
            ) {

                closeTooltip(
                    currentOpenTrigger,
                    tooltip,
                    false
                );
            }
        }
    );
    // KEYDOWN on document: keep Esc global fallback
    document.addEventListener('keydown', function (e) {
        const key = e.key;

        if (key === 'Escape' || key === 'Esc') {
            const openTriggerEl = currentOpenTrigger ||
                document.querySelector(TRIGGER_SELECTOR + ' + ' + TOOLTIP_SELECTOR + '.' + OPEN_CLASS)?.previousElementSibling ||
                null;

            if (openTriggerEl) {
                const openTooltipEl = getTooltipForTrigger(openTriggerEl);
                closeTooltip(openTriggerEl, openTooltipEl);
            }
        }
    });

    // Initial ARIA sync + bind per-element keyboard handlers
    const triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    triggers.forEach(t => {
        const tip = getTooltipForTrigger(t);
        syncAria(t, tip, false);

        // Make sure it's keyboard-activatable
        if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '0');
        if (!t.hasAttribute('role')) t.setAttribute('role', 'button');

        // Enter/Space toggles when focus is on the trigger
        t.addEventListener('keydown', function (e) {
            const key = e.key;
            if (key === 'Enter' || key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                toggleTooltip(t);
            } else if (key === 'Escape' || key === 'Esc') {
                const tooltip = getTooltipForTrigger(t);
                closeTooltip(t, tooltip);
            }
        });
    });

    // Also support keyboard on the close button if present
    const closeButtons = document.querySelectorAll(CLOSE_SELECTOR);
    closeButtons.forEach(btn => {
        if (!btn.hasAttribute('tabindex')) btn.setAttribute('tabindex', '0');
        if (!btn.hasAttribute('role')) btn.setAttribute('role', 'button');

        btn.addEventListener('keydown', function (e) {
            const key = e.key;
            if (key === 'Enter' || key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                const tooltipEl = closest(btn, TOOLTIP_SELECTOR);
                const relTrigger = tooltipEl && tooltipEl.previousElementSibling &&
                    matches(tooltipEl.previousElementSibling, TRIGGER_SELECTOR)
                    ? tooltipEl.previousElementSibling
                    : null;
                closeTooltip(relTrigger, tooltipEl);
            }
        });
    });
})();
